import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (
    process.env.NODE_ENV === 'production'
      ? 'https://clinic-mvp-backend.onrender.com'
      : 'http://127.0.0.1:8000'
  ),
});

// Interceptor para agregar token
API.interceptors.request.use((req) => {
  const token = localStorage.getItem('token');
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

// Interceptor para manejar errores globales
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token inválido o vencido. Si había un token guardado, la sesión
      // estaba activa y expiró en medio del uso — dejamos una marca para
      // que Login.js avise "tu sesión expiró" en vez de mostrar el form en
      // blanco sin explicación. No navegamos por acá: useAuth.js reacciona
      // al 401 de GET /users/me actualizando su estado (user=null), y
      // ProtectedRoute deja que React Router navegue de forma normal —
      // un window.location.href disparado desde este interceptor durante
      // el mount inicial de la página competía en una carrera con esa
      // navegación de React Router y a veces perdía, terminando en "/"
      // en vez de "/login" con el aviso.
      const hadActiveSession = !!localStorage.getItem('token');
      if (hadActiveSession) {
        sessionStorage.setItem('session_expired', '1');
      }
      localStorage.removeItem('token');
    }
    // 403 = autenticado pero sin permiso para ese recurso — NO cerrar sesión
    // Normalizar mensaje de error para uso en componentes
    const detail = error.response?.data?.detail;
    error.userMessage = Array.isArray(detail)
      ? detail.map((d) => (typeof d === 'string' ? d : d.msg || d.message || 'Error de validación')).filter(Boolean).join(', ')
      : (typeof detail === 'string' ? detail : null) || error.message || 'Error inesperado';
    // Errores 422 de Pydantic traen `loc: ["body", "nombre_campo"]` por cada
    // error — se arma un mapa campo→mensaje para que los formularios puedan
    // marcar la caja específica en rojo en vez de un Alert genérico arriba.
    // `loc` puede tener más niveles (ej. ["body", "items", 0, "amount"]) —
    // se toma el último segmento string como nombre de campo.
    error.fieldErrors = {};
    if (Array.isArray(detail)) {
      for (const d of detail) {
        const loc = d?.loc;
        if (!Array.isArray(loc)) continue;
        const field = [...loc].reverse().find((seg) => typeof seg === 'string' && seg !== 'body');
        if (field) error.fieldErrors[field] = d.msg || 'Valor inválido';
      }
    }
    return Promise.reject(error);
  }
);

// ────────────────────────────────────────────────
// Auth
// ────────────────────────────────────────────────
export const login = async (data) => {
  const formData = new FormData();
  formData.append('username', data.username);
  formData.append('password', data.password);

  return API.post('/users/login', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const getMe = () => API.get('/users/me');
export const changePassword = (data) => API.put('/users/me/password', data);

// ────────────────────────────────────────────────
// Superadmin — control total cross-clínica para soporte
// ────────────────────────────────────────────────
export const listAllClinics = () => API.get('/superadmin/clinics');
export const switchToClinic = (clinicId) => API.post(`/superadmin/switch/${clinicId}`);
export const updateClinicPlan = (clinicId, plan) => API.put(`/superadmin/clinics/${clinicId}/plan`, { plan });
export const exitSupportSession = () => API.post('/superadmin/exit');

// ────────────────────────────────────────────────
// Link de autocompletado de datos del paciente (patient_self_update)
// ────────────────────────────────────────────────
export const generateSelfUpdateLink = (patientId = null) =>
  API.post('/patient-self-update/generate-link', { patient_id: patientId });
// Los dos de abajo son públicos (sin token de sesión de staff) — el
// interceptor de arriba igual intenta mandar el header Authorization si
// hay uno guardado, pero el backend no lo exige para estas rutas.
export const getSelfUpdateForm = (token) => API.get(`/patient-self-update/${encodeURIComponent(token)}`);
export const submitSelfUpdateForm = (token, data) => API.post(`/patient-self-update/${encodeURIComponent(token)}`, data);

// ────────────────────────────────────────────────
// Clínicas
// ────────────────────────────────────────────────
export const createClinic = (data) => API.post('/clinics/', data);
export const getMyClinic = () => API.get('/clinics/me');
export const updateMyClinic = (data) => API.put('/clinics/me', data);
export const getMyClinicFeatures = () => API.get('/clinics/me/features');
export const updateMyClinicFeatures = (features) => API.put('/clinics/me/features', { features });
export const exportClinicData = () => API.get('/clinics/me/export', { responseType: 'blob' });
export const generateDicomApiKey = () => API.post('/clinics/me/dicom-api-key');

export const uploadClinicLogo = (file) => {
  const form = new FormData();
  form.append('file', file);
  return API.post('/clinics/me/logo', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const downloadClinicLogo = () => API.get('/clinics/me/logo', { responseType: 'blob' });
export const deleteClinicLogo = () => API.delete('/clinics/me/logo');

// ────────────────────────────────────────────────
// Usuarios (registro)
// ────────────────────────────────────────────────
export const createUser = (data) => API.post('/users/', data);

// ────────────────────────────────────────────────
// Doctores
// ────────────────────────────────────────────────
export const createDoctor = (data) => API.post('/doctors/', data);
export const getDoctors = (params = {}) => API.get('/doctors/', { params });
export const getDoctor = (id) => API.get(`/doctors/${id}`);
export const updateDoctor = (id, data) => API.put(`/doctors/${id}`, data);
export const deleteDoctor = (id) => API.delete(`/doctors/${id}`);
export const getDoctorSchedule = (id) => API.get(`/doctors/${id}/schedule`);
export const getDoctorAvailableSlots = (id, params = {}) => API.get(`/doctors/${id}/available-slots`, { params });
export const updateDoctorSchedule = (id, slots) => API.put(`/doctors/${id}/schedule`, slots);
export const getMyDoctorProfile = () => API.get('/doctors/me');
export const updateMyDoctorProfile = (data) => API.put('/doctors/me', data);

// ────────────────────────────────────────────────
// Pacientes
// ────────────────────────────────────────────────
export const createPatient = (data, source) => API.post('/patients/', data, { params: source ? { source } : {} });
export const getPatients = (params = {}) => API.get('/patients/', { params });
export const getPatient = (id) => API.get(`/patients/${id}`);
export const updatePatient = (id, data) => API.put(`/patients/${id}`, data);
export const deletePatient = (id) => API.delete(`/patients/${id}`);

export const uploadPatientPhoto = (patientId, file) => {
  const form = new FormData();
  form.append('file', file);
  return API.post(`/patients/${patientId}/photo`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const downloadPatientPhoto = (patientId) =>
  API.get(`/patients/${patientId}/photo`, { responseType: 'blob' });

export const deletePatientPhoto = (patientId) =>
  API.delete(`/patients/${patientId}/photo`);
export const searchPatientsByDni = (dni) => API.get('/patients/', { params: { search: dni, limit: 1 } });
export const exportPatients = (params = {}) =>
  API.get('/patients/export', { params, responseType: 'blob' });

export const createPatientInsurance = (patientId, data) => API.post(`/patients/${patientId}/insurances`, data);
export const updatePatientInsurance = (patientId, insuranceId, data) => API.put(`/patients/${patientId}/insurances/${insuranceId}`, data);
export const deletePatientInsurance = (patientId, insuranceId) => API.delete(`/patients/${patientId}/insurances/${insuranceId}`);

// ────────────────────────────────────────────────
// Turnos (Appointments)
// ────────────────────────────────────────────────
export const createAppointment = (data) => API.post('/appointments/', data);
export const getAppointments = (params = {}) => API.get('/appointments/', { params });
export const updateAppointment = (id, data) => API.put(`/appointments/${id}`, data);
export const deleteAppointment = (id) => API.delete(`/appointments/${id}`);
export const exportAppointments = (params = {}) =>
  API.get('/appointments/export', { params, responseType: 'blob' });
export const printDailyAppointments = (params = {}) =>
  API.get('/appointments/print', { params, responseType: 'blob' });
export const getAppointmentHistory = (id) => API.get(`/appointments/${id}/history`);
export const sendAppointmentEmail = (id, emailType) =>
  API.post(`/appointments/${id}/send-email`, null, { params: { email_type: emailType } });

// Sala de espera
export const getWaitingRoom = () => API.get('/appointments/waiting-room');
export const checkInAppointment = (id, data = {}) => API.post(`/appointments/${id}/checkin`, data);
export const updateCheckin = (id, data) => API.put(`/appointments/${id}/checkin`, data);
export const removeCheckin = (id) => API.delete(`/appointments/${id}/checkin`);

// Lista de espera (standby)
export const getWaitlist = (params = {}) => API.get('/waitlist/', { params });
export const createWaitlistEntry = (data) => API.post('/waitlist/', data);
export const updateWaitlistEntry = (id, data) => API.put(`/waitlist/${id}`, data);
export const deleteWaitlistEntry = (id) => API.delete(`/waitlist/${id}`);

// ────────────────────────────────────────────────
// Historia clínica / Comentarios
// ────────────────────────────────────────────────
export const createComment = (patientId, data) =>
  API.post(`/history/patients/${patientId}/comments`, data);

export const getComments = (patientId) =>
  API.get(`/history/patients/${patientId}/comments`);

// ────────────────────────────────────────────────
// Historia clínica estructurada
// ────────────────────────────────────────────────
export const getMedicalRecords = (patientId, params = {}) =>
  API.get(`/history/patients/${patientId}/records`, { params });
export const createMedicalRecord = (patientId, data) =>
  API.post(`/history/patients/${patientId}/records`, data);
export const updateMedicalRecord = (patientId, recordId, data) =>
  API.put(`/history/patients/${patientId}/records/${recordId}`, data);
export const downloadMedicalHistoryPdf = (patientId) =>
  API.get(`/history/patients/${patientId}/records/pdf`, { responseType: 'blob' });

// ────────────────────────────────────────────────
// Plantillas de consulta médica
// ────────────────────────────────────────────────
export const getConsultationTemplates = () => API.get('/templates/consultation/');
export const createConsultationTemplate = (data) => API.post('/templates/consultation/', data);
export const updateConsultationTemplate = (id, data) => API.put(`/templates/consultation/${id}`, data);
export const deleteConsultationTemplate = (id) => API.delete(`/templates/consultation/${id}`);

// ────────────────────────────────────────────────
// Plantillas WhatsApp
// ────────────────────────────────────────────────
export const getWhatsAppTemplates = () => API.get('/templates/whatsapp/');
export const createWhatsAppTemplate = (data) => API.post('/templates/whatsapp/', data);
export const updateWhatsAppTemplate = (id, data) => API.put(`/templates/whatsapp/${id}`, data);
export const deleteWhatsAppTemplate = (id) => API.delete(`/templates/whatsapp/${id}`);

// ────────────────────────────────────────────────
// Plantillas de órdenes de estudio
// ────────────────────────────────────────────────
export const getStudyOrderTemplates = () => API.get('/templates/study-orders/');
export const createStudyOrderTemplate = (data) => API.post('/templates/study-orders/', data);
export const updateStudyOrderTemplate = (id, data) => API.put(`/templates/study-orders/${id}`, data);
export const deleteStudyOrderTemplate = (id) => API.delete(`/templates/study-orders/${id}`);

// ────────────────────────────────────────────────
// Plantillas de email (confirmación/recordatorio/cancelación de turno)
// ────────────────────────────────────────────────
export const getEmailTemplates = () => API.get('/templates/email/');
export const updateEmailTemplate = (templateType, data) => API.put(`/templates/email/${templateType}`, data);
export const resetEmailTemplate = (templateType) => API.delete(`/templates/email/${templateType}`);

// ────────────────────────────────────────────────
// Auditoría
// ────────────────────────────────────────────────
export const getAuditLog = (params = {}) => API.get('/audit/', { params });

// ────────────────────────────────────────────────
// Métricas
// ────────────────────────────────────────────────
export const getMetrics = () => API.get('/metrics/');

// ────────────────────────────────────────────────
// Usuarios (gestión admin)
// ────────────────────────────────────────────────
export const getUsers = () => API.get('/users/');
export const updateUserRole = (id, data) => API.put(`/users/${id}`, data);
export const adminResetPassword = (id, newPassword) => API.put(`/users/${id}/reset-password`, { new_password: newPassword });
export const adminForceVerifyEmail = (id) => API.put(`/users/${id}/force-verify-email`);

// ────────────────────────────────────────────────
// Facturación
// ────────────────────────────────────────────────
export const getBillingList = (params = {}) => API.get('/billing/', { params });
export const createPayment = (data) => API.post('/billing/', data);
export const updatePayment = (id, data) => API.put(`/billing/${id}`, data);
export const deletePayment = (id) => API.delete(`/billing/${id}`);
export const getBillingStats = (params = {}) => API.get('/billing/stats/summary', { params });
export const exportBilling = (params = {}) =>
  API.get('/billing/export', { params, responseType: 'blob' });

// ────────────────────────────────────────────────
// Archivos de HC
// ────────────────────────────────────────────────
export const getRecordFiles = (recordId) =>
  API.get(`/files/records/${recordId}`);

export const uploadRecordFile = (recordId, file) => {
  const form = new FormData();
  form.append('file', file);
  return API.post(`/files/records/${recordId}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const downloadRecordFile = (fileId) =>
  API.get(`/files/${fileId}/download`, { responseType: 'blob' });

export const deleteRecordFile = (fileId) =>
  API.delete(`/files/${fileId}`);

export const renameRecordFile = (fileId, displayName) =>
  API.put(`/files/${fileId}`, { display_name: displayName });

// ────────────────────────────────────────────────
// Estudios complementarios (nivel paciente)
// ────────────────────────────────────────────────
export const getPatientStudies = (patientId, params = {}) =>
  API.get(`/files/patients/${patientId}/studies`, { params });

export const uploadPatientStudy = (patientId, file, meta = {}) => {
  const form = new FormData();
  form.append('file', file);
  form.append('category', meta.category || 'otro');
  if (meta.description) form.append('description', meta.description);
  if (meta.study_date) form.append('study_date', meta.study_date);
  if (meta.group_id) form.append('group_id', meta.group_id);
  return API.post(`/files/patients/${patientId}/studies`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Agrega un archivo más al estudio del que anchorFileId forma parte — ej.
// el informe que llega unos días después de las imágenes. anchorFileId
// puede ser cualquier archivo del grupo, o el único archivo de un estudio
// todavía sin agrupar (el backend le crea el group_id en el momento).
export const addFileToStudyGroup = (anchorFileId, file) => {
  const form = new FormData();
  form.append('file', file);
  return API.post(`/files/studies/${anchorFileId}/files`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const downloadPatientStudy = (fileId) =>
  API.get(`/files/studies/${fileId}/download`, { responseType: 'blob' });

export const deletePatientStudy = (fileId) =>
  API.delete(`/files/studies/${fileId}`);

export const updatePatientStudy = (fileId, meta = {}) =>
  API.put(`/files/studies/${fileId}`, meta);

// Estudios subidos desde DENTRO de una consulta (marcados como "es un
// estudio complementario" al adjuntar) — se listan junto a los archivos de
// la nota, y también aparecen en la pestaña Estudios del paciente.
export const getRecordStudies = (recordId) =>
  API.get(`/files/records/${recordId}/studies`);

export const uploadRecordStudy = (recordId, file, meta = {}) => {
  const form = new FormData();
  form.append('file', file);
  form.append('category', meta.category || 'otro');
  if (meta.description) form.append('description', meta.description);
  return API.post(`/files/records/${recordId}/studies`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// ────────────────────────────────────────────────
// Ingesta automática de estudios DICOM (feature flag dicom_studies)
// ────────────────────────────────────────────────
export const getPendingDicomStudies = () => API.get('/dicom-ingest/pending');
export const assignPendingDicomStudy = (fileId, patientId) =>
  API.put(`/dicom-ingest/pending/${fileId}/assign`, null, { params: { patient_id: patientId } });

// ────────────────────────────────────────────────
// Recetas electrónicas
// ────────────────────────────────────────────────
export const searchDrugs = (q = '', form = '') => API.get('/prescriptions/drugs/search', { params: { q, ...(form ? { form } : {}), limit: 20 } });

export const getPrescriptions = (params = {}) => API.get('/prescriptions/', { params });
export const getPrescription = (id) => API.get(`/prescriptions/${id}`);
export const createPrescription = (data) => API.post('/prescriptions/', data);
export const updatePrescription = (id, data) => API.put(`/prescriptions/${id}`, data);
export const deletePrescription = (id) => API.delete(`/prescriptions/${id}`);
export const emitPrescription = (id) => API.post(`/prescriptions/${id}/emit`);
export const downloadPrescriptionPdf = (id) =>
  API.get(`/prescriptions/${id}/pdf`, { responseType: 'blob' });
export const verifyPrescription = (token) =>
  API.get('/prescriptions/verify', { params: { token } });
export const getPrescriptionPublicKey = () =>
  API.get('/prescriptions/public-key');

// ────────────────────────────────────────────────
// Órdenes de estudio
// ────────────────────────────────────────────────
export const getStudyOrders = (params = {}) => API.get('/study-orders/', { params });
export const getStudyOrder = (id) => API.get(`/study-orders/${id}`);
export const createStudyOrder = (data) => API.post('/study-orders/', data);
export const updateStudyOrder = (id, data) => API.put(`/study-orders/${id}`, data);
export const deleteStudyOrder = (id) => API.delete(`/study-orders/${id}`);
export const emitStudyOrder = (id) => API.post(`/study-orders/${id}/emit`);
export const downloadStudyOrderPdf = (id) =>
  API.get(`/study-orders/${id}/pdf`, { responseType: 'blob' });
export const verifyStudyOrder = (token) =>
  API.get('/study-orders/verify', { params: { token } });

// Genérica
export const apiGet = (endpoint, params = {}) => API.get(endpoint, { params });

// ────────────────────────────────────────────────
// Problemas del paciente
// ────────────────────────────────────────────────
export const getPatientProblems = (patientId) => API.get(`/problems/patients/${patientId}`);
export const createPatientProblem = (patientId, data) => API.post(`/problems/patients/${patientId}`, data);
export const updatePatientProblem = (patientId, problemId, data) => API.put(`/problems/patients/${patientId}/${problemId}`, data);
export const deletePatientProblem = (patientId, problemId) => API.delete(`/problems/patients/${patientId}/${problemId}`);

// Mensajes / comunicaciones enviadas al paciente
// ────────────────────────────────────────────────
export const getPatientMessages = (patientId) => API.get(`/messages/patients/${patientId}`);
export const logPatientMessage  = (patientId, data) => API.post(`/messages/patients/${patientId}`, data);
export const getAllMessages = (params = {}) => API.get('/messages/', { params });

// ────────────────────────────────────────────────
// Email / recordatorios WhatsApp pendientes
// ────────────────────────────────────────────────
export const getPendingWhatsAppReminders = () => API.get('/internal/pending-whatsapp-reminders');
export const markReminderSent = (appointmentId) => API.post(`/internal/mark-reminder-sent/${appointmentId}`);

// ────────────────────────────────────────────────
// Password reset
// ────────────────────────────────────────────────
export const forgotPassword = (email) => API.post('/users/forgot-password', { email });
export const resetPassword = (token, newPassword) => API.post('/users/reset-password', { token, new_password: newPassword });
export const verifyEmail = (token) => API.post('/users/verify-email', { token });
export const resendVerification = (email) => API.post('/users/resend-verification', { email });
// ────────────────────────────────────────────────
// Notificaciones in-app
// ────────────────────────────────────────────────
export const getNotifications = () => API.get('/notifications/');

// ────────────────────────────────────────────────
// Algoritmos (árboles de decisión interactivos)
// ────────────────────────────────────────────────
export const getDecisionTrees = (params = {}) => API.get('/decision-trees/', { params });
export const getDecisionTree = (id) => API.get(`/decision-trees/${id}`);
export const createDecisionTree = (data) => API.post('/decision-trees/', data);
export const updateDecisionTree = (id, data) => API.put(`/decision-trees/${id}`, data);
export const deleteDecisionTree = (id) => API.delete(`/decision-trees/${id}`);

export const createDecisionNode = (treeId, data) => API.post(`/decision-trees/${treeId}/nodes`, data);
export const updateDecisionNode = (treeId, nodeId, data) => API.put(`/decision-trees/${treeId}/nodes/${nodeId}`, data);
export const deleteDecisionNode = (treeId, nodeId) => API.delete(`/decision-trees/${treeId}/nodes/${nodeId}`);

export const createDecisionOption = (treeId, nodeId, data) => API.post(`/decision-trees/${treeId}/nodes/${nodeId}/options`, data);
export const updateDecisionOption = (treeId, optionId, data) => API.put(`/decision-trees/${treeId}/options/${optionId}`, data);
export const deleteDecisionOption = (treeId, optionId) => API.delete(`/decision-trees/${treeId}/options/${optionId}`);

export const uploadDecisionNodeMedia = (treeId, nodeId, file, caption) => {
  const form = new FormData();
  form.append('file', file);
  if (caption) form.append('caption', caption);
  return API.post(`/decision-trees/${treeId}/nodes/${nodeId}/media`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const deleteDecisionNodeMedia = (mediaId) => API.delete(`/decision-trees/media/${mediaId}`);
export const downloadDecisionNodeMedia = (mediaId) =>
  API.get(`/decision-trees/media/${mediaId}/download`, { responseType: 'blob' });

// ────────────────────────────────────────────────
// Tipos de turno
// ────────────────────────────────────────────────
export const getAppointmentTypes = (params = {}) => API.get('/appointment-types/', { params });
export const createAppointmentType = (data) => API.post('/appointment-types/', data);
export const updateAppointmentType = (id, data) => API.put(`/appointment-types/${id}`, data);
export const deleteAppointmentType = (id) => API.delete(`/appointment-types/${id}`);

export const getConsultationTypes = (params = {}) => API.get('/consultation-types/', { params });
export const createConsultationType = (data) => API.post('/consultation-types/', data);
export const updateConsultationType = (id, data) => API.put(`/consultation-types/${id}`, data);
export const deleteConsultationType = (id) => API.delete(`/consultation-types/${id}`);

// ────────────────────────────────────────────────
// Portal del paciente — auth propia y separada de la de staff (token de
// portal, no el "token" de localStorage que usa el interceptor de arriba),
// así que estas funciones arman el header Authorization a mano en vez de
// depender del interceptor global.
// ────────────────────────────────────────────────
export const requestPortalAccess = (email) => API.post('/patient-portal/request-access', { email });
export const portalLogin = (token) => API.post('/patient-portal/login', { token });
const portalAuthHeader = (portalToken) => ({ headers: { Authorization: `Bearer ${portalToken}` } });
export const getPortalAppointments = (portalToken) => API.get('/patient-portal/appointments', portalAuthHeader(portalToken));
export const cancelPortalAppointment = (portalToken, appointmentId) =>
  API.post(`/patient-portal/appointments/${appointmentId}/cancel`, null, portalAuthHeader(portalToken));
